import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { getPayCentralCalendarChartDataAction } from "../../Actions/PaycentralAction";
import { getCalendarChartDataAction } from "../../Actions/TransactionActions";
import NoDataFound from "./NoDataFound";
import { MyCancelIcon } from "../../Assets/svg/MyIcons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DashboardActivityGraph = ({
  selectedMerchant,
  selectedWebsite,
  selectedTimeRange,
  startDate,
  endDate,
  showButton,
  onRemove,
  onShowSnackbar,
  dashboardType,
  tenantType // ✅ IMPORTANT
}) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });

  const [loading, setLoading] = useState(false);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
      title: { display: true, text: "Transaction Activity" },
    },
    scales: {
      x: { title: { display: true, text: "Date / Month" } },
      y: {
        title: {
          display: true,
          text: "Total Transaction Amount ($)",
        },
        beginAtZero: true,
      },
    },
  }), []);

  const fetchChartData = useCallback(async () => {
    setLoading(true);

    try {
      let pId = selectedMerchant?.pId || null;
      let qId = selectedMerchant?.qId || null;
      let tId = selectedMerchant?.tId || null;
      let kId = selectedMerchant?.kId || null;

      let qbotRes = null;
      let paycentralRes = null;
      let tripolaRes = null;
      let kycRes = null;

      const promises = [];

      // ✅ QBOT
      if (tenantType === "all" || tenantType === "qid") {
        promises.push(
          getCalendarChartDataAction({
            startDate,
            endDate,
            merchant: qId,
            chartType: selectedTimeRange,
            websiteId: selectedWebsite || null,
          }).then(res => { qbotRes = res; })
        );
      }

      // ✅ PAYCENTRAL
      if (tenantType === "all" || tenantType === "pid") {
        promises.push(
          getPayCentralCalendarChartDataAction({
            startDate,
            endDate,
            tenantId: pId || "all",
            chartType: selectedTimeRange,
            websiteId: selectedWebsite || null,
          }).then(res => { paycentralRes = res; })
        );
      }

      // ✅ TRIPOLA
      // if ((tenantType === "all" || tenantType === "tid") && tId && getTripolaCalendarChartDataAction) {
      //   promises.push(
      //     getTripolaCalendarChartDataAction({
      //       startDate,
      //       endDate,
      //       tenantId: tId,
      //       chartType: selectedTimeRange,
      //     }).then(res => { tripolaRes = res; })
      //   );
      // }

      // // ✅ KYC
      // if ((tenantType === "all" || tenantType === "kid") && kId && getKycCalendarChartDataAction) {
      //   promises.push(
      //     getKycCalendarChartDataAction({
      //       startDate,
      //       endDate,
      //       tenantId: kId,
      //       chartType: selectedTimeRange,
      //     }).then(res => { kycRes = res; })
      //   );
      // }

      await Promise.allSettled(promises);

      // ✅ LABELS (merge all)
      const allLabels = [
        ...(qbotRes?.data?.labels || []),
        ...(paycentralRes?.data?.labels || []),
        ...(tripolaRes?.data?.labels || []),
        ...(kycRes?.data?.labels || [])
      ];

      const labels = [...new Set(allLabels)].sort();

      // ✅ MAP FUNCTION (important)
      const mapData = (labels, sourceLabels = [], sourceData = []) => {
        const map = {};
        sourceLabels.forEach((l, i) => {
          map[l] = sourceData[i];
        });
        return labels.map(l => map[l] || 0);
      };

      // ✅ DATASETS
      const datasets = [];

      if ((tenantType === "all" || tenantType === "qid") && qbotRes) {
        datasets.push({
          label: "Qbot",
          data: mapData(labels, qbotRes?.data?.labels, qbotRes?.data?.data),
          backgroundColor: "#1565C0",
        });
      }

      if ((tenantType === "all" || tenantType === "pid") && paycentralRes) {
        datasets.push({
          label: "PayCentral",
          data: mapData(labels, paycentralRes?.data?.labels, paycentralRes?.data?.data),
          backgroundColor: "#2E7D32",
        });
      }

      if ((tenantType === "all" || tenantType === "tid") && tripolaRes) {
        datasets.push({
          label: "Tripola",
          data: mapData(labels, tripolaRes?.data?.labels, tripolaRes?.data?.data),
          backgroundColor: "#EF6C00",
        });
      }

      if ((tenantType === "all" || tenantType === "kid") && kycRes) {
        datasets.push({
          label: "KYC",
          data: mapData(labels, kycRes?.data?.labels, kycRes?.data?.data),
          backgroundColor: "#6A1B9A",
        });
      }
      // console.log("QBOT FULL:", qbotRes);
      // console.log("PAYCENTRAL FULL:", paycentralRes);

      setChartData({
        labels,
        datasets,
      });

    } catch (error) {
      console.error(error);
      onShowSnackbar("Failed to load activity chart", "error");
    } finally {
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    selectedMerchant,
    selectedWebsite,
    selectedTimeRange,
    tenantType,
    onShowSnackbar
  ]);

  useEffect(() => {
    if (startDate && endDate && tenantType) {
      fetchChartData();
    }
  }, [startDate, endDate, selectedMerchant, selectedWebsite, tenantType, fetchChartData]);

  const isNoData =
    !chartData.datasets.length ||
    chartData.datasets.every(ds =>
      !ds.data.length || ds.data.every(v => v === 0)
    );

  return (
    <div className={`kpi-card ${!showButton && "my-3"}`} key="transactionActivityChart">
      {showButton && (
        <MyCancelIcon
          className="close-icon position-absolute cursor-pointer dash-close z-index-3"
          style={{ top: 5, right: 5 }}
          onClick={() => onRemove("transactionActivityChart")}
        />
      )}

      <div className="kpi-card-content">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="chart-title m-0">Total Activity</h2>
        </div>

        <div className="chart-canvas-container" style={{ height: "350px" }}>
          {loading ? (
            <div className="chart-loading">
              <CircularProgress />
              <p>Loading calendar data...</p>
            </div>
          ) : isNoData ? (
            <NoDataFound message="No Transaction Activity data available" />
          ) : (
            <Bar data={chartData} options={chartOptions} />
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardActivityGraph;